import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable, Subject, interval, of } from 'rxjs';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Loading } from '@components/ui/loading/loading';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { MsfSelect } from '@components/select/select';
import { DataMonitoringBoardComponent } from './components/data-monitoring-board/data-monitoring-board';
import { DataMonitoringYearPanelComponent } from './components/data-monitoring-year-panel/data-monitoring-year-panel';
import { BoardView, YearPanelMonth, YearPanelRowTarget } from './data-monitoring-view.model';
import { yearlyAtdDropdownLabel } from '@app/pages/weather/pages/gis-weather/components/weather-legend/legend-match.util';
import { OperationMonitoringService as OperationMonitoringApiService } from '@6grain/masofa-monolith-api-client/common/api/operationMonitoring.service';
import { DataMatrixSnapshot } from '@6grain/masofa-monolith-api-client/common/model/dataMatrixSnapshot';
import {
  CellState,
  DATA_MONITORING_REFRESH_MS,
  FORECAST_DAYS_AHEAD,
  GROUP_LABEL_KEYS,
  MatrixGroupKey,
  buildMatrix,
  currentUtcDay,
  defaultMonth,
  groupDayState,
  monthDays,
  monthSummaries,
  problemDays,
  sourceFailures,
  weekdayOf,
} from './data-monitoring-matrix.util';

type LoadRequest = { year: number; quiet: boolean };
type LoadResult = { request: LoadRequest; snapshot: DataMatrixSnapshot | null };

const GLYPHS: Record<CellState, string> = { ok: '', warn: '…', problem: '✕', none: '' };
const HIGHLIGHT_MS = 2500;
const YEARS_BACK = 10;
const TOP_ROWS_PER_MONTH = 2;

/**
 * Data monitoring matrix (docs/ORCHESTRATION.md §8): every monitored parameter by day, one month at a time,
 * with a year panel of problem counts. Holds the state; the board and the year panel only draw it.
 */
@Component({
  selector: 'app-data-monitoring-page',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    Loading,
    SecondaryButton,
    MsfSelect,
    DataMonitoringBoardComponent,
    DataMonitoringYearPanelComponent,
  ],
  templateUrl: './data-monitoring-page.html',
  styleUrl: './data-monitoring-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataMonitoringPageComponent implements OnInit, OnDestroy {
  private readonly monitoring = inject(OperationMonitoringApiService);
  private readonly translate = inject(TranslateService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroy$ = new Subject<void>();
  private readonly load$ = new Subject<LoadRequest>();
  private highlightTimer: ReturnType<typeof setTimeout> | undefined;

  readonly today = signal(currentUtcDay());
  readonly thisYear = computed(() => Number(this.today().slice(0, 4)));
  readonly year = signal(Number(currentUtcDay().slice(0, 4)));
  readonly month = signal(Number(currentUtcDay().slice(5, 7)) - 1);
  readonly snapshot = signal<DataMatrixSnapshot | null>(null);
  readonly loading = signal(true);
  readonly loadFailed = signal(false);
  readonly updatedAt = signal<Date | null>(null);
  readonly collapsed = signal<ReadonlySet<MatrixGroupKey>>(new Set());
  readonly highlightedRow = signal<string | null>(null);
  readonly currentLang = signal(this.translate.currentLang);

  readonly refreshMinutes = DATA_MONITORING_REFRESH_MS / 60_000;
  readonly forecastDays = FORECAST_DAYS_AHEAD - 1;
  readonly forecastHorizon = FORECAST_DAYS_AHEAD;

  readonly yearOptions = computed(() =>
    Array.from({ length: YEARS_BACK + 1 }, (_, i) => {
      const value = this.thisYear() - i;
      return { label: String(value), value };
    }));

  readonly matrix = computed(() => {
    const data = this.snapshot();
    return data ? buildMatrix(data, data.year ?? this.year(), this.today()) : null;
  });

  readonly failures = computed(() => {
    const data = this.snapshot();
    return data ? sourceFailures(data) : [];
  });

  readonly summaries = computed(() => {
    const matrix = this.matrix();
    return matrix ? monthSummaries(matrix) : [];
  });

  readonly totalProblems = computed(() => this.summaries().reduce((sum, item) => sum + item.problems, 0));

  readonly updatedText = computed(() => {
    const lang = this.currentLang();
    const at = this.updatedAt();
    return at ? at.toLocaleString(lang || undefined, { dateStyle: 'short', timeStyle: 'short' }) : '';
  });

  readonly monthNameKey = computed(() => `primeng.monthNames.${this.month()}`);
  readonly isFirstMonth = computed(() => this.month() === 0);
  readonly isLastMonth = computed(() => this.month() === 11);

  /** Everything the board draws for the selected month, translated, so the template calls no methods. */
  readonly board = computed((): BoardView | null => {
    this.currentLang();
    const matrix = this.matrix();
    if (!matrix) {
      return null;
    }
    const today = this.today();
    const days = monthDays(matrix.year, this.month());
    const collapsed = this.collapsed();
    const highlighted = this.highlightedRow();
    const header = days.map(iso => {
      const weekday = weekdayOf(iso);
      return {
        iso,
        day: Number(iso.slice(8, 10)),
        weekdayKey: `primeng.dayNamesShort.${weekday}`,
        weekend: weekday === 0 || weekday === 6,
        today: iso === today,
      };
    });
    const groups = matrix.groups.map(group => {
      const isCollapsed = collapsed.has(group.key);
      return {
        key: group.key,
        labelKey: GROUP_LABEL_KEYS[group.key],
        collapsed: isCollapsed,
        problems: group.rows.reduce((sum, row) => sum + problemDays(row, days), 0),
        cells: days.map(iso => {
          const state = groupDayState(group, iso);
          return { iso, state, glyph: GLYPHS[state] };
        }),
        rows: isCollapsed ? [] : group.rows.map(row => {
          const label = this.rowLabel(row.label, row.translateLabel);
          return {
            key: row.key,
            label,
            highlighted: row.key === highlighted,
            problems: problemDays(row, days),
            cells: days.map(iso => {
              const cell = row.cells[iso] ?? { state: 'none' as CellState, note: null };
              return {
                iso,
                state: cell.state,
                glyph: GLYPHS[cell.state],
                title: cell.state === 'none' ? null : this.cellTitle(label, iso, cell.note, cell.count),
              };
            }),
          };
        }),
      };
    });
    return { header, groups };
  });

  /** Right-hand panel: problems per month and the worst rows of each, in calendar order. */
  readonly yearPanelMonths = computed((): YearPanelMonth[] => {
    this.currentLang();
    const matrix = this.matrix();
    if (!matrix) {
      return [];
    }
    const groupOf = new Map(matrix.groups.flatMap(group => group.rows.map(row => [row.key, group.key] as const)));
    const thisMonthStart = `${this.today().slice(0, 7)}-01`;
    const daysShort = this.translate.instant('DATA_MONITORING_DAYS_SHORT');
    return this.summaries().map(summary => {
      const firstDay = monthDays(matrix.year, summary.month)[0];
      return {
        month: summary.month,
        nameKey: `primeng.monthNames.${summary.month}`,
        problems: summary.problems,
        future: firstDay > thisMonthStart,
        selected: summary.month === this.month(),
        topRows: summary.rows.slice(0, TOP_ROWS_PER_MONTH).map(entry => ({
          key: entry.row.key,
          group: groupOf.get(entry.row.key) ?? 'reports',
          text: `${this.rowLabel(entry.row.label, entry.row.translateLabel)} ${entry.days} ${daysShort}`,
        })),
        more: Math.max(0, summary.rows.length - TOP_ROWS_PER_MONTH),
      };
    });
  });

  /** The panel as listed: the latest started month on top and back to January, the months still ahead below them. */
  readonly yearPanel = computed((): YearPanelMonth[] => {
    const months = this.yearPanelMonths();
    return [...months.filter(item => !item.future).reverse(), ...months.filter(item => item.future)];
  });

  ngOnInit(): void {
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(event => {
      this.currentLang.set(event.lang);
    });

    this.load$.pipe(
      tap(request => this.beginLoad(request)),
      switchMap(request => this.fetch(request)),
      takeUntil(this.destroy$),
    ).subscribe(result => this.applyResult(result));

    interval(DATA_MONITORING_REFRESH_MS).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.load$.next({ year: this.year(), quiet: true });
    });

    this.load$.next({ year: this.year(), quiet: false });
  }

  ngOnDestroy(): void {
    clearTimeout(this.highlightTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }

  refresh(): void {
    this.load$.next({ year: this.year(), quiet: true });
  }

  selectYear(value: number): void {
    if (value === this.year()) {
      return;
    }
    this.year.set(value);
    this.load$.next({ year: value, quiet: false });
  }

  previousMonth(): void {
    this.month.update(month => Math.max(0, month - 1));
  }

  nextMonth(): void {
    this.month.update(month => Math.min(11, month + 1));
  }

  openMonth(month: number): void {
    this.month.set(month);
  }

  toggleGroup(key: MatrixGroupKey): void {
    this.collapsed.update(current => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  /** Opens the month, unfolds the row's group, scrolls to the row and highlights it for a moment. */
  goToRow({ month, group, rowKey }: YearPanelRowTarget): void {
    this.month.set(month);
    this.collapsed.update(current => {
      const next = new Set(current);
      next.delete(group);
      return next;
    });
    this.highlightedRow.set(rowKey);
    clearTimeout(this.highlightTimer);
    this.highlightTimer = setTimeout(() => this.highlightedRow.set(null), HIGHLIGHT_MS);
    setTimeout(() => {
      const target = Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>('[data-row-key]'))
        .find(element => element.dataset['rowKey'] === rowKey);
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  private beginLoad(request: LoadRequest): void {
    this.today.set(currentUtcDay());
    if (!request.quiet) {
      this.loading.set(true);
      this.snapshot.set(null);
    }
  }

  private fetch(request: LoadRequest): Observable<LoadResult> {
    return this.monitoring.monitoringDataMatrixGet(request.year).pipe(
      map(snapshot => ({ request, snapshot })),
      catchError(() => of({ request, snapshot: null })),
    );
  }

  private applyResult({ request, snapshot }: LoadResult): void {
    this.loading.set(false);
    if (!snapshot) {
      // A quiet reload keeps the last board; the error shows above it.
      this.loadFailed.set(true);
      return;
    }
    this.loadFailed.set(false);
    this.snapshot.set(snapshot);
    this.updatedAt.set(new Date());
    if (!request.quiet) {
      this.month.set(defaultMonth(this.summaries(), request.year, this.today()));
    }
  }

  private rowLabel(label: string, translateLabel: boolean): string {
    // Yearly layer titles carry the unit on a second line; the GIS dropdown shows only the first one.
    return translateLabel ? yearlyAtdDropdownLabel(this.translate.instant(label)) : label;
  }

  private cellTitle(label: string, iso: string, note: string | null, count: number | undefined): string {
    const parts = [`${label} · ${iso}`];
    if (note) {
      parts.push(this.translate.instant(`DATA_MONITORING_NOTE_${toScreamingSnake(note)}`));
    }
    if (count !== undefined) {
      parts.push(this.translate.instant('DATA_MONITORING_SCENES', { count }));
    }
    return parts.join(' — ');
  }
}

function toScreamingSnake(value: string): string {
  return value.replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase();
}
